import { useCallback, useContext, useRef } from 'react'

import { getCartToastActions } from '../../../components/cart/CartToastActions'
import { InCheckoutContext } from '../../../layouts/context'
import { useUniqueToast } from '../../toasts/hooks/useUniqueToast'
import { ADD_TO_CART_SUCCESS_TOAST_ID_PREFIX, ADD_TO_CART_TOAST_ID_PREFIX } from '../../toasts/ids'
import { ToggleCartContext } from '../context'

export interface ShowSuccessToastOptions {
  skus?: string[]
}

export interface ShowFailureToastOptions {
  skus?: string[]
  additionalMessage?: string
}

export interface ShowRemovedToastOptions {
  skus?: string[]
}

export interface UseAddToCartToast {
  showSuccessToast: (options: ShowSuccessToastOptions) => void
  showFailureToast: (options: ShowFailureToastOptions) => void
  showRemovedToast: (options: ShowRemovedToastOptions) => void
}

const getSuccessMessage = (skus?: string[]): string => {
  if (!skus || skus.length === 0) {
    return 'Successfully added to cart.'
  }
  if (skus.length > 3) {
    const [first, second, ...rest] = skus
    return `${first}, ${second} and ${rest.length} more successfully added to cart.`
  }
  return `${skus.join(', ')} successfully added to cart.`
}

const getFailureMessage = (skus?: string[]): string => {
  if (!skus || skus.length === 0) {
    return 'Failed to add to cart.'
  }
  return `${skus.join(', ')} failed to add to cart.`
}

const getRemovedMessage = (skus?: string[]) =>
  !skus || skus.length === 0
    ? 'An item in your cart cannot be purchased at this time and has been removed.'
    : `${skus.join(', ')} cannot be purchased at this time and has been removed from your cart.`

export const useAddToCartToast = (): UseAddToCartToast => {
  // Two views of one group. The warnings keep the group prefix, so showing one still clears whatever
  // add-to-cart toast was up; successes are minted under the nested prefix, which is all the cart
  // clears when it opens (see `useDismissAddToCartToastOnCartOpen`).
  const {
    showToast: showWarningToast,
    hideToast: hideAddToCartToasts,
    hideToastById,
  } = useUniqueToast({ toastIdPrefix: ADD_TO_CART_TOAST_ID_PREFIX })
  const { showToast: showAddedToast } = useUniqueToast({
    toastIdPrefix: ADD_TO_CART_SUCCESS_TOAST_ID_PREFIX,
  })
  const inCheckout = useContext(InCheckoutContext)
  // Read through a ref, not the captured value: an add is announced from the resolution of a Magento
  // mutation, so the `showSuccessToast` that runs was captured when the shopper hit Add to Cart and
  // its `isCartOpen` is that moment's. The drawer usually opens during the round trip, which is the
  // case this check exists for.
  const { isCartOpen } = useContext(ToggleCartContext)
  const isCartOpenRef = useRef(isCartOpen)
  isCartOpenRef.current = isCartOpen

  // Two surfaces say it better than the toast can, and both already list what was added: checkout's
  // order summary and review list, and the open cart drawer. The toast's `Show Cart` action is
  // useless on either - checkout renders no mini cart, and the drawer is the mini cart.
  //
  // Suppressing on an open cart is also what covers the add that resolves *after* the drawer opens
  // (sc-144370): `useDismissAddToCartToastOnCartOpen` clears on the closed -> open transition, so a
  // toast arriving later has no transition left to catch it and would sit over the drawer's footer
  // and Secure Checkout button for its full autohide.
  //
  // Failures still surface, since those are not otherwise visible.
  const showSuccessToast = useCallback(
    ({ skus }: ShowSuccessToastOptions) => {
      if (inCheckout || isCartOpenRef.current) {
        return
      }
      const message = getSuccessMessage(skus)
      // The nested prefix only replaces earlier successes, so clear the warnings too - one
      // add-to-cart toast at a time, as before.
      hideAddToCartToasts()
      showAddedToast(message, {
        variant: 'success',
        action: getCartToastActions({ dismissToast: hideToastById }),
      })
    },
    [hideAddToCartToasts, hideToastById, inCheckout, showAddedToast],
  )

  const showFailureToast = useCallback(
    ({ skus, additionalMessage }: ShowFailureToastOptions) => {
      const message = `${getFailureMessage(skus)}${
        additionalMessage ? ` ${additionalMessage}` : ''
      }`
      showWarningToast(message, {
        variant: 'warning',
        persist: true,
        action: getCartToastActions({ dismissToast: hideToastById }),
      })
    },
    [hideToastById, showWarningToast],
  )

  const showRemovedToast = useCallback(
    ({ skus }: ShowRemovedToastOptions) => {
      showWarningToast(getRemovedMessage(skus), {
        variant: 'warning',
        persist: true,
        action: getCartToastActions({ dismissToast: hideToastById }),
      })
    },
    [hideToastById, showWarningToast],
  )

  return { showSuccessToast, showFailureToast, showRemovedToast }
}
